import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';
// Import Shared Components
import { Input } from 'shared-components';
// Import Utils
import {
  SECURITY_QUESTION_MAX_LENGTH,
  SECURITY_QUESTION_MIN_LENGTH,
  SECURITY_QUESTION_PRESETS,
  normalizeSecurityQuestion,
} from '../../utils/securityQuestion';

const CUSTOM = 'custom';

const DefaultHelp = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  opacity: 0.8;
`;

// The global styles remove the browser's own select arrow, so draw one over the right edge.
const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SelectArrow = styled(ChevronDown)`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const DefaultError = styled.div`
  margin-top: 6px;
  color: red;
  font-size: 12px;
`;

// The e-Transfer security question: a list of ready-made questions plus "Write my own…",
// with guidance and the length limit shown up front. The parent owns the question text and
// validates it with validateSecurityQuestion; this component only reports the text through
// onChange. inputComponent, helpComponent and errorComponent let a screen keep its own look;
// dropdownComponent swaps the native select for a custom one (options of { id, name }).
const SecurityQuestionField = ({
  id,
  value,
  onChange,
  onBlur = undefined,
  error = null,
  invalidProps = {},
  inputComponent: InputComponent = Input,
  helpComponent: Help = DefaultHelp,
  errorComponent: ErrorText = DefaultError,
  dropdownComponent: Dropdown = null,
}) => {
  const intl = useIntl();
  const [choice, setChoice] = useState(() => {
    if (!value) return '';
    const preset = SECURITY_QUESTION_PRESETS.find(key => intl.messages[key] === value);
    return preset || CUSTOM;
  });

  // A preset is stored as its text, so re-send it in the new language when the locale changes.
  useEffect(() => {
    if (choice && choice !== CUSTOM && intl.messages[choice] !== value) {
      onChange(intl.messages[choice]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intl.locale]);

  const selectChoice = next => {
    setChoice(next);
    onChange(next && next !== CUSTOM ? intl.messages[next] : '');
  };

  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = error ? `${hintId} ${errorId}` : hintId;
  const options = [
    ...SECURITY_QUESTION_PRESETS.map(key => ({ id: key, name: intl.messages[key] })),
    { id: CUSTOM, name: intl.messages['security-question-custom-option'] },
  ];

  return (
    <>
      {Dropdown ? (
        <Dropdown
          id={id}
          options={options}
          selectedOption={options.find(option => option.id === choice) || null}
          onSelect={option => selectChoice(option.id)}
          placeholder={intl.messages['security-question-select-placeholder']}
          invalid={Boolean(error) && choice !== CUSTOM}
          ariaDescribedBy={describedBy}
        />
      ) : (
        <SelectWrapper>
          <InputComponent
            as="select"
            id={id}
            value={choice}
            onChange={e => selectChoice(e.target.value)}
            onBlur={choice === CUSTOM ? undefined : onBlur}
            aria-invalid={Boolean(error) && choice !== CUSTOM}
            aria-describedby={describedBy}
            style={{ paddingRight: '40px', cursor: 'pointer' }}
            {...(choice !== CUSTOM ? invalidProps : {})}
          >
            <option value="" disabled>
              {intl.messages['security-question-select-placeholder']}
            </option>
            {SECURITY_QUESTION_PRESETS.map(key => (
              <option key={key} value={key}>
                {intl.messages[key]}
              </option>
            ))}
            <option value={CUSTOM}>{intl.messages['security-question-custom-option']}</option>
          </InputComponent>
          <SelectArrow size={16} aria-hidden="true" />
        </SelectWrapper>
      )}
      {choice === CUSTOM && (
        <InputComponent
          id={`${id}-custom`}
          type="text"
          value={value}
          onChange={e => onChange(normalizeSecurityQuestion(e.target.value))}
          onBlur={onBlur}
          aria-label={intl.messages['security-question-custom-label']}
          placeholder={intl.messages['security-question-custom-label']}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          style={{ marginTop: '8px' }}
          {...invalidProps}
        />
      )}
      <Help id={hintId}>
        {intl.messages['security-question-guidance']}
        {' '}
        {intl.formatMessage(
          { id: 'security-question-hint' },
          { min: SECURITY_QUESTION_MIN_LENGTH, max: SECURITY_QUESTION_MAX_LENGTH }
        )}
      </Help>
      {error && (
        <ErrorText id={errorId} role="alert">
          {error}
        </ErrorText>
      )}
    </>
  );
};

SecurityQuestionField.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.node,
  // Props applied to whichever control is showing when the question is invalid, so each
  // screen can use its own invalid styling (a className or a styled-components prop).
  invalidProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  inputComponent: PropTypes.elementType,
  helpComponent: PropTypes.elementType,
  errorComponent: PropTypes.elementType,
  dropdownComponent: PropTypes.elementType,
};

export default SecurityQuestionField;
